import apiV2 from '../api/v2';
import type { PaginatedMeta } from './accounts';

// Accounts > Users. Backed by the admin-only /api/v2/users endpoints (plus
// the /users/wordpress sub-resource for WordPress account lifecycle). This
// is a *separate* directory from the practice-scoped Users tab
// (services/practiceUsers.ts) - that one lists/invites/unlinks users for a
// single practice, this one is the global cross-practice admin table.

// The paginated list (GET /users) selects a narrower column set than every
// other user-returning endpoint here - it never returns lastSeenRelease, and
// its `practices` field is an enriched { id, name } array (a join against
// the practices table). GET /users/:email, POST /users, and PUT /users/:email
// all return the full raw DB row instead (see UserRecord below), where
// `practices` is still the raw string[] of ids. The two shapes genuinely
// differ - don't assume they're interchangeable.
export interface UserPractice {
    id: string;
    name: string | null;
}

export interface UserListItem {
    id: string;
    email: string;
    fullname: string;
    isAdmin: boolean;
    practices: UserPractice[];
    createdAt: string;
    lastActivityAt: string | null;
}

// Full raw-row shape returned by the single-user GET, and by create/update.
export interface UserRecord {
    id: string;
    email: string;
    fullname: string;
    isAdmin: boolean;
    practices: string[];
    createdAt: string;
    lastActivityAt: string | null;
    lastSeenRelease: string | null;
}

export type UsersListMeta = PaginatedMeta;

export type UsersSortBy = 'name' | 'email' | 'date' | 'activity';
export type UsersSortOrder = 'asc' | 'desc';
export type UsersRoleFilter = 'admin' | 'user';

export interface UsersListParams {
    page: number;
    limit: number;
    search?: string;
    sort?: UsersSortBy;
    order?: UsersSortOrder;
    /** Practice ids to filter by - sent as a CSV list on the wire. */
    practice?: string[];
    role?: UsersRoleFilter;
}

export interface CreateUserInput {
    email: string;
    fullname: string;
    isAdmin?: boolean;
    practices?: string[];
}

export interface UpdateUserInput {
    email?: string;
    fullname?: string;
    isAdmin?: boolean;
    practices?: string[];
}

// The WP REST user resource, as returned by POST /users/wordpress. Mirrors
// backend's WordpressUser interface (services/platforms/wordpress/authentication.ts).
export interface WordpressAccount {
    id: number;
    username: string;
    name: string;
    email: string;
    roles: string[];
}

export async function fetchUsers(
    params: UsersListParams
): Promise<{ data: UserListItem[]; meta: UsersListMeta }> {
    const query: Record<string, string | string[]> = {
        page: String(params.page),
        limit: String(params.limit)
    };
    if (params.search) query.search = params.search;
    if (params.sort) query.sort = params.sort;
    if (params.order) query.order = params.order;
    if (params.practice?.length) query.practice = params.practice;
    if (params.role) query.role = params.role;

    const response = await apiV2.get<{
        success: true;
        data: UserListItem[];
        meta: UsersListMeta;
    }>('/users', { params: query });
    return { data: response.data.data, meta: response.data.meta };
}

// Access > Teams' member picker: every user in a scope, filtered client-side
// (see AddTeamMemberForm). No `limit` here - the backend's users.service.ts
// skips pagination entirely whenever `scope` is set and returns the full
// directory, so a page-size param on this call would be misleading.
export async function fetchTeamUsers(scope: 'odo' | 'practice'): Promise<UserListItem[]> {
    const response = await apiV2.get<{
        success: true;
        data: UserListItem[];
        meta: UsersListMeta;
    }>('/users', { params: { scope } });
    return response.data.data;
}

export async function fetchUserByEmail(email: string): Promise<UserRecord> {
    const response = await apiV2.get<{ success: true; data: UserRecord }>(
        `/users/${encodeURIComponent(email)}`
    );
    return response.data.data;
}

export async function createUser(input: CreateUserInput): Promise<UserRecord> {
    const response = await apiV2.post<{ success: true; data: UserRecord }>('/users', input);
    return response.data.data;
}

export async function updateUser(email: string, patch: UpdateUserInput): Promise<UserRecord> {
    const response = await apiV2.put<{ success: true; data: UserRecord }>(
        `/users/${encodeURIComponent(email)}`,
        patch
    );
    return response.data.data;
}

export async function deleteUser(email: string): Promise<void> {
    await apiV2.delete(`/users/${encodeURIComponent(email)}`);
}

export async function createWordpressAccount(input: {
    email: string;
    fullname: string;
}): Promise<WordpressAccount> {
    const response = await apiV2.post<{ success: true; data: WordpressAccount }>(
        '/users/wordpress',
        input
    );
    return response.data.data;
}

export async function deleteWordpressAccount(email: string): Promise<void> {
    await apiV2.delete(`/users/wordpress/${encodeURIComponent(email)}`);
}

export async function sendPasswordReset(email: string): Promise<void> {
    await apiV2.post('/users/password-reset', { email });
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/**
 * Reads the WordPress error code the backend forwards at
 * `error.details.wordpressCode` (see wordpress/errors.ts's fromAxiosError) -
 * used to tell "this WP account already exists" apart from a real failure.
 */
function extractWordpressCode(err: unknown): string | undefined {
    if (!isRecord(err)) return undefined;
    const response = err.response;
    if (!isRecord(response)) return undefined;
    const data = response.data;
    if (!isRecord(data) || !isRecord(data.error)) return undefined;
    const details = data.error.details;
    if (!isRecord(details)) return undefined;
    return typeof details.wordpressCode === 'string' ? details.wordpressCode : undefined;
}

function getStatusCode(err: unknown): number | undefined {
    if (!isRecord(err)) return undefined;
    const response = err.response;
    if (!isRecord(response)) return undefined;
    return typeof response.status === 'number' ? response.status : undefined;
}

const EXISTING_WORDPRESS_USER_CODES = new Set(['existing_user_login', 'existing_user_email']);

/**
 * Full "create a user" orchestration, mirroring internal's Users.tsx
 * handleSave: WordPress account first (an 'existing_user_login' /
 * 'existing_user_email' failure means the WP account is already there, so
 * it's swallowed rather than aborting the flow), then the DB record, then an
 * optional password-reset email. Any other WP failure aborts before the DB
 * write - it's surfaced to the caller to toast.
 */
export async function createUserWithWordpress(
    input: CreateUserInput,
    options?: { sendPasswordReset?: boolean }
): Promise<UserRecord> {
    try {
        await createWordpressAccount({ email: input.email, fullname: input.fullname });
    } catch (wpError) {
        const wpCode = extractWordpressCode(wpError);
        if (!wpCode || !EXISTING_WORDPRESS_USER_CODES.has(wpCode)) {
            throw wpError;
        }
    }

    const created = await createUser(input);

    if (options?.sendPasswordReset) {
        await sendPasswordReset(input.email);
    }

    return created;
}

export interface DeleteUserResult {
    /**
     * True when the DB user was removed but the WordPress account could not
     * be (a non-404 failure) - the caller should toast a "may need manual
     * cleanup" message rather than a plain success, matching internal's
     * handleDelete.
     */
    wordpressCleanupFailed: boolean;
}

/**
 * Full "delete a user" orchestration, mirroring internal's Users.tsx
 * handleDelete: DB record first, then the WP account - a 404 there (already
 * gone) is swallowed, any other failure is reported back via the result
 * instead of throwing, since the DB deletion already succeeded.
 */
export async function deleteUserWithWordpress(email: string): Promise<DeleteUserResult> {
    await deleteUser(email);

    try {
        await deleteWordpressAccount(email);
        return { wordpressCleanupFailed: false };
    } catch (wpError) {
        if (getStatusCode(wpError) === 404) {
            return { wordpressCleanupFailed: false };
        }
        return { wordpressCleanupFailed: true };
    }
}
